import { MenuBoard, MoneySend, SmsTracking, UserSearch } from 'iconsax-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { AppRoutes } from '@/constants/appRoutes'
import env from '@/env/env'
import {
  EUserPermission,
  EUserRole,
} from '@/hooks/usePermissions/permissions.type'
import { usePermissions } from '@/hooks/usePermissions/usePermissions'
import { SidebarCredits } from '@/pages/LeftSideBar/components/sidebarCredits/SidebarCredits'
import { SidebarLogout } from '@/pages/LeftSideBar/components/sidebarLogout/sidebarLogout'
import { SidebarUser } from '@/pages/LeftSideBar/components/sidebarUser/sidebarUser'
import { WecheckEnviromentDistinction } from '@/pages/LeftSideBar/components/WecheckEnviromentDistinction/WecheckEnviromentDistinction'
import { SocialMirrorPalette } from '@/utils/palette/SocialMirrorPalette'

interface AppSidebarProps {
  userData: any
  isLoadingUserData: boolean
  isPending: boolean
  userFullname: string
  userFirstLetter: string
  credits: number
}

export function AppSidebar({
  userData,
  isLoadingUserData,
  isPending,
  userFullname,
  userFirstLetter,
  credits,
}: AppSidebarProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const { hasPermission } = usePermissions()
  const enviroment = env.NODE_ENV

  const { isMobile, setOpenMobile } = useSidebar()

  useEffect(() => {
    if (isMobile) {
      setOpenMobile(false)
    }
  }, [location.pathname, isMobile, setOpenMobile])

  const menuItems = [
    {
      name: t('dashboard.sidebar.runSearch'),
      url: AppRoutes.dashboard,
      icon: <UserSearch size="24" color={SocialMirrorPalette.neutralColor} />,
      isActive:
        location.pathname === AppRoutes.dashboard ||
        location.pathname === AppRoutes.generatingReport,
    },
    {
      name: t('dashboard.sidebar.reportHistory'),
      url: AppRoutes.reports,
      icon: <MenuBoard size="25" color={SocialMirrorPalette.neutralColor} />,
      isActive:
        location.pathname === AppRoutes.reports ||
        location.pathname === AppRoutes.digitalReport ||
        location.pathname === AppRoutes.socialMediaProfile,
      disabled: !hasPermission(EUserPermission.VIEW_REPORTS, EUserRole.READER),
    },
    {
      name: t('dashboard.sidebar.billingAndInvoices'),
      url: AppRoutes.billingAndInvoices,
      icon: <MoneySend size="20" color={SocialMirrorPalette.neutralColor} />,
      isActive: location.pathname === AppRoutes.billingAndInvoices,
      disabled: false,
      show: hasPermission(EUserPermission.HANDLE_CREDITS, EUserRole.OWNER),
    },
    {
      name: t('dashboard.sidebar.support'),
      url: AppRoutes.contactUs,
      icon: <SmsTracking size="20" color={SocialMirrorPalette.neutralColor} />,
      isActive: location.pathname === AppRoutes.contactUs,
      disabled: false,
    },
  ]

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <WecheckEnviromentDistinction enviroment={enviroment} />
      </SidebarHeader>

      <SidebarContent className="bg-baseColor">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems
                .filter((item) => item.show !== false)
                .map((item) => (
                  <SidebarMenuItem key={item.name}>
                    <SidebarMenuButton
                      asChild
                      isActive={item.isActive}
                      disabled={item.disabled}
                      tooltip={item.name}
                      className={`
                        h-10 rounded-md
                        ${
                          item.isActive && !item.disabled
                            ? 'bg-neutralColor5 hover:bg-baseColorHighlight'
                            : ''
                        }
                        ${
                          !item.disabled && !item.isActive
                            ? 'bg-baseColor hover:bg-baseColorHighlight'
                            : ''
                        }
                        ${
                          item.disabled
                            ? 'bg-baseColor text-neutralColor30 cursor-not-allowed opacity-50'
                            : 'text-neutralColor'
                        }
                      `}
                    >
                      {item.disabled ? (
                        <div className="flex items-center gap-2">
                          {item.icon}
                          <span className="text-xs font-medium group-data-[collapsible=icon]:hidden text-neutralColor30">
                            {item.name}
                          </span>
                        </div>
                      ) : (
                        <Link to={item.url} className="flex items-center gap-2">
                          {item.icon}
                          <span
                            className={`text-xs font-medium group-data-[collapsible=icon]:hidden text-neutralColor`}
                          >
                            {item.name}
                          </span>
                        </Link>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="bg-baseColor">
        <SidebarUser
          userInitials={userFirstLetter}
          name={userFullname}
          mail={userData.email}
          isPending={isPending}
          profileImgUrl={userData.profilePicUrl}
          isEarlyAdopter={userData.account.isEarlyAdopter}
        />

        <SidebarCredits
          credits={credits}
          impersonationCredits={userData?.account?.impersonationCredits ?? 0}
          isLoading={isLoadingUserData}
        />

        <SidebarLogout />
      </SidebarFooter>
    </Sidebar>
  )
}
